/* eslint-disable @typescript-eslint/ban-ts-comment */
// Globals
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, getFeedbacks, selectQuery } from '../../feature';

// Models
import { FeedbackType } from '@/models/feedback.model';

// Components
import { RangePicker } from '@/components';
import { Input, Select } from '@/ui';
import { StyledFilterRow } from './ContactSupportFiltersContainerStyles';

// Helpers
import { GetFeedbacksDTO } from '../../dtos';
import { getStatusTypeOptions } from '../../helpers/getOptions';
import { FeedbacksFiltersFields } from '../../helpers/types';
import { useManagersOptions } from '../../hooks';

type IFeedbacksFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const ContactSupportFiltersContainer = ({ children, className }: IFeedbacksFiltersContainerProps) => {
  const { t } = useTranslation(['feedbacks']);
  const dispatch = useAppDispatch();
  const query = useAppState(selectQuery);
  const managersOptions = useManagersOptions();

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetFeedbacksDTO({ ...searchParams, type: FeedbackType.CONTACT_PAGE });
        dispatch(getFeedbacks(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleDateChange = (_: any, dates: [string, string]) => {
    const newQuery = {
      ...query,
      [FeedbacksFiltersFields.DATE_FROM]: dates?.[0] || undefined,
      [FeedbacksFiltersFields.DATE_TO]: dates?.[1] || undefined,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleSelectFilter = (name: string) => (value: string) => {
    const newQuery = {
      ...query,
      [name]: value || undefined,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Input
            placeholder={t('label_name')}
            name={FeedbacksFiltersFields.NAME}
            onChange={handleSearch}
            value={query?.[FeedbacksFiltersFields.NAME]?.toString()}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('label_phone')}
            name={FeedbacksFiltersFields.PHONE}
            onChange={handleSearch}
            value={query?.[FeedbacksFiltersFields.PHONE]?.toString()}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('label_comment')}
            name={FeedbacksFiltersFields.COMMENT}
            onChange={handleSearch}
            value={query?.[FeedbacksFiltersFields.COMMENT]?.toString()}
            size="large"
            allowClear
          />
        </th>
        <th>
          <RangePicker
            placeholder={t('label_date')}
            name={FeedbacksFiltersFields.DATE_FROM}
            allowEmpty={[true, true]}
            onChange={handleDateChange}
            value={[
              // @ts-ignore
              query?.[FeedbacksFiltersFields.DATE_FROM] ? dayjs(query?.[FeedbacksFiltersFields.DATE_FROM]) : null,
              // @ts-ignore
              query?.[FeedbacksFiltersFields.DATE_TO] ? dayjs(query?.[FeedbacksFiltersFields.DATE_TO]) : null,
            ]}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Select
            size="large"
            allowClear
            placeholder={t('label_manager')}
            value={query?.[FeedbacksFiltersFields.MANAGER_ID]}
            onChange={handleSelectFilter(FeedbacksFiltersFields.MANAGER_ID)}
            options={managersOptions}
          />
        </th>
        <th>
          <Select
            size="large"
            allowClear
            placeholder={t('label_status')}
            value={query?.[FeedbacksFiltersFields.STATUS]}
            onChange={handleSelectFilter(FeedbacksFiltersFields.STATUS)}
            options={getStatusTypeOptions(t)}
          />
        </th>
      </StyledFilterRow>
    </thead>
  );
};

export default memo(ContactSupportFiltersContainer);
